import instance from "./api";

export interface IFASReportFilter {
  startDate?: string;
  endDate?: string;
  branchId?: number;
  branch?: string;
  province?: string;
  opsCenterId?: number;
  operationsCentre?: string;
  funeralPlannerId?: string;
  plannerName?: string;
  funeralType?: string;
  status?: string;
  funeralDate?: string;
  page?: number;
  size?: number;
}

export interface FuneralRegisterReport {
  funeralId: string; funeralNumber: string; deceasedName: string; deceasedIdNumber: string;
  branchName: string; opsCentreName: string; funeralPlannerName: string; funeralType: string;
  status: string; funeralDate?: string; cemeteryName?: string; venueName?: string;
  createdAt?: string; turnaroundDays: number;
}

export interface PlannerPerformanceReport {
  plannerId: string; plannerName: string; branchName: string; opsCentreName: string;
  totalAssigned: number; activeCount: number; completedCount: number; cancelledCount: number;
  avgTurnaroundDays: number; dailyCount: number; weeklyCount: number; monthlyCount: number;
  quarterlyCount: number; ytdCount: number;
}

export interface MortuaryOccupancyReport {
  mortuaryId: number; mortuaryName: string; branchName: string; province: string; capacity: number;
  currentBodiesCount: number; availableCapacity: number; occupancyPercentage: number;
  bodiesReceivedPeriod: number; bodiesReleasedPeriod: number; bodiesAwaitingRelease: number;
  avgStorageDurationDays: number;
}

export interface ServiceCateringReport {
  serviceId: string; funeralNumber: string; deceasedName: string; clientName: string;
  opsCentreName: string; plannerName: string; funeralDate?: string; category: string;
  serviceDetails: string; guestCount: number; totalCost: number | string; status: string;
}

export interface FleetLogisticsReport {
  allocationId: string; funeralNumber: string; registrationNumber: string; vehicleType: string;
  driverName: string; opsCentreName: string; allocationDate?: string; routeDetails: string;
  bodyRemovalsCount: number; logisticsRequirement: string; status: string;
}

export interface WarehouseInventoryReport {
  itemId: string; itemCode: string; itemName: string; category: string; warehouseName: string;
  currentStock: number; minThreshold: number; allocatedQuantity: number; dispatchedQuantity: number;
  returnedQuantity: number; varianceQuantity: number; unitPrice: number | string;
  supplierName: string; status: string; lastMovementDate?: string;
}

export interface IFASExecutiveDashboard {
  totalFuneralsPeriod: number; activeFuneralsCount: number; completedFuneralsCount: number;
  dailyScheduledFunerals: number; weeklyCompletedFunerals: number; totalMortuaries: number;
  totalMortuaryCapacity: number; currentTotalBodies: number; overallMortuaryOccupancyPct: number;
  activeFleetVehicles: number; totalBodyRemovalsPeriod: number; lowStockAlertsCount: number;
  topPlanners: PlannerPerformanceReport[]; mortuaryBreakdown: MortuaryOccupancyReport[];
  funeralsByOpsCentre: Record<string, number>; funeralsByPlanner: Record<string, number>;
  funeralsByPackage: Record<string, number>;
}

export interface IFASAdditionalReport {
  reportType: string; referenceNumber: string; name: string; category: string; location: string;
  status: string; date?: string; quantity?: number; amount?: number | string; metric?: number; details?: string;
}

const get = async <T>(path: string, filter?: IFASReportFilter): Promise<T> => {
  const response = await instance.get<T>(path, { params: filter });
  return response.data;
};

export const ifasReportsService = {
  getFuneralRegisterReports: (filter?: IFASReportFilter) =>
    get<FuneralRegisterReport[]>("/v1/ifas-reports/funeral-register", filter),
  getPlannerPerformanceReports: (filter?: IFASReportFilter) =>
    get<PlannerPerformanceReport[]>("/v1/ifas-reports/planner-performance", filter),
  getMortuaryOccupancyReports: (filter?: IFASReportFilter) =>
    get<MortuaryOccupancyReport[]>("/v1/ifas-reports/mortuary-occupancy", filter),
  getServiceCateringReports: (filter?: IFASReportFilter) =>
    get<ServiceCateringReport[]>("/v1/ifas-reports/special-services", filter),
  getFleetLogisticsReports: (filter?: IFASReportFilter) =>
    get<FleetLogisticsReport[]>("/v1/ifas-reports/fleet-logistics", filter),
  getWarehouseInventoryReports: (filter?: IFASReportFilter) =>
    get<WarehouseInventoryReport[]>("/v1/ifas-reports/warehouse-inventory", filter),
  getExecutiveDashboard: (filter?: IFASReportFilter) =>
    get<IFASExecutiveDashboard>("/v1/ifas-reports/executive-dashboard", filter),
  getInventoryMovementReports: (filter?: IFASReportFilter) =>
    get<IFASAdditionalReport[]>("/v1/ifas-reports/inventory-movements", filter),
  getSupplierUtilisationReports: (filter?: IFASReportFilter) =>
    get<IFASAdditionalReport[]>("/v1/ifas-reports/supplier-utilisation", filter),
  getCustomerAnalysisReports: (filter?: IFASReportFilter) =>
    get<IFASAdditionalReport[]>("/v1/ifas-reports/customer-analysis", filter),
  getFeedbackReports: (filter?: IFASReportFilter) =>
    get<IFASAdditionalReport[]>("/v1/ifas-reports/feedback", filter),
  getMortuaryTrendReports: (filter?: IFASReportFilter) =>
    get<IFASAdditionalReport[]>("/v1/ifas-reports/mortuary-trends", filter),

  downloadReportExport: async (
    reportType: string,
    format: "excel" | "pdf" | "csv",
    filter?: IFASReportFilter,
  ) => {
    const response = await instance.get("/v1/ifas-reports/export", {
      params: { reportType, format, ...filter },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(response.data);
    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = `ifas_${reportType}_report.${format === "excel" ? "xlsx" : format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      window.URL.revokeObjectURL(url);
    }
  },
};
